import { groupCrossesByEticket } from 'features/transaction-details/helpers/authorizationTickets';
import { ICrossWithServiceInfo } from 'features/transaction-details/types';

export interface LockedCrossOperator {
	operatorCode: string;
}

/** Si ya hay un e-ticket cruzado activo, el resto de cruces debe usar el mismo operador. */
export const getLockedCrossOperatorForAuthorization = (
	crosses: Pick<ICrossWithServiceInfo, 'eticket' | 'operator' | 'amount' | 'state' | 'createdAt'>[],
): LockedCrossOperator | null => {
	const crossedTickets = groupCrossesByEticket(crosses as ICrossWithServiceInfo[]).filter(
		(ticket) => ticket.status === 'crossed' && ticket.netAmount > 0,
	);

	if (crossedTickets.length === 0) return null;

	const operatorCodes = [
		...new Set(
			crossedTickets
				.map((ticket) => ticket.activeCross?.operator ?? ticket.operator)
				.filter((code): code is string => Boolean(code)),
		),
	];

	if (operatorCodes.length === 0) return null;

	return { operatorCode: operatorCodes[0] };
};

export const getOperatorDisplayName = (
	operatorCode: string,
	operators: { value: string; description: string }[],
): string => {
	const match = operators.find((op) => op.value === operatorCode);
	return match?.description ?? operatorCode;
};

export const buildLockedOperatorMessage = (operatorName: string): string =>
	`Solo podés usar ${operatorName}: esta autorización ya tiene un cruce con ese operador en otro e-ticket.`;

export const parseCrossAmountInput = (value: unknown): number | null => {
	const amount = parseFloat(String(value ?? ''));
	return Number.isFinite(amount) && amount > 0 ? amount : null;
};

/** Por debajo de este saldo pendiente, el aviso de cruce parcial se muestra en rojo. */
export const LOW_PENDING_AFTER_CROSS_THRESHOLD = 100;

export const computeRemainingPendingAfterCross = (
	pendingToCross: number,
	amountEntered: number | null,
): number | null => {
	if (pendingToCross <= 0 || amountEntered == null) return null;
	if (amountEntered >= pendingToCross) return null;
	return pendingToCross - amountEntered;
};

export const isLowRemainingPendingAfterCross = (remaining: number): boolean =>
	remaining > 0 && remaining < LOW_PENDING_AFTER_CROSS_THRESHOLD;
